import type { CSSProperties } from 'react'
import type { EasyDrawerProps, EasyDrawerState } from './declare'
import { useEffect, useMemo, useContext, createContext } from 'react'
import { reaction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { Drawer } from 'antd'
import { uuid } from 'src/utils/tools'

const DrawerPropsContext = createContext<EasyDrawerProps>({} as EasyDrawerProps)
const DrawerContext = createContext<EasyDrawerState>({} as EasyDrawerState)

const DrawerMain = observer(() => {
  const props = useContext(DrawerPropsContext)
  const state = useContext(DrawerContext)

  const handleClose = (evt: any) => {
    props.drawer?.onClose?.(evt)
    state.setVisible(false)
  }

  const styles = useMemo(() => {
    return {
      header: { padding: '12px 16px' },
      body: { padding: '12px 16px' }
    }
  }, [])

  return (
    <Drawer
      destroyOnClose
      maskClosable={false}
      styles={styles}
      contentWrapperStyle={{ maxWidth: 1600 }}
      {...props.drawer}
      {...state.drawer}
      width={props.fullDrawer ? '100%' : (props.drawer?.width || state.drawer.width)}
      open={state.visible}
      onClose={handleClose}
      className={`${state.uniqueClass} ${props.className}`}
    >
      {props.children}
    </Drawer>
  )
})

const DrawerSlot = observer(() => {
  const props = useContext(DrawerPropsContext)
  const state = useContext(DrawerContext)

  const handleClick = () => {
    !props.disabled && state.setVisible(true)
  }

  const className = useMemo(() => {
    return `easy-drawer__slot ${props.slotClass}`
  }, [props.slotClass])

  const style = useMemo<CSSProperties>(() => {
    return {
      display: 'inline-block',
      lineHeight: 1,
      cursor: 'pointer',
      ...props.slotStyle
    }
  }, [props.slotStyle])

  return <div onClick={handleClick} className={className} style={style}>{props.slot}</div>
})

const EasyDrawer = (props: EasyDrawerProps) => {
  const state = useLocalObservable<EasyDrawerState>(() => {
    return {
      initDone: true,
      uniqueClass: uuid(),
      drawer: { width: '80%' },
      updateDrawer (drawer) {
        Object.assign(this.drawer, drawer)
      },
      visible: false,
      setVisible (visible) {
        this.visible = visible
      },
      show () {
        this.setVisible(true)
      },
      close (data?: any) {
        this.setVisible(false)
        props.onClose?.(data) // 用于一些弹窗关闭需要回调的场景用
      },
    }
  })

  useEffect(() => {
    state.setVisible(!!props.visible)

    const dispose = reaction(
      () => state.visible,
      () => props.onVisibleChange?.(state.visible)
    )

    return () => dispose()
  }, [props.visible])

  return (
    <DrawerPropsContext.Provider value={props}>
      <DrawerContext.Provider value={state}>
        <DrawerSlot />
        <DrawerMain />
      </DrawerContext.Provider>
    </DrawerPropsContext.Provider>
  )
}

export { DrawerContext }
export default EasyDrawer